import { useAppMutation } from '@/shared/hooks/useAppMutation'
import { ScreenplayEntity } from '@/shared/lib/db'
import { AppError } from '@/shared/types/AppError'
import { HookOptions } from '@/shared/types/HookOptions'
import { useQuery } from '@tanstack/react-query'
import { screenplayService } from '../services/screenplay.service'

export const screenplayKeys = {
    all: ['screenplays'] as const,
    list: (projectId: string) => [...screenplayKeys.all, 'list', projectId] as const,
    detail: (id: string) => [...screenplayKeys.all, 'detail', id] as const,
    byTitle: (projectId: string, title: string) => [...screenplayKeys.all, 'byTitle', projectId, title] as const,
}

export const useScreenplaysQuery = (projectId?: string) => {
    return useQuery({
        queryKey: projectId ? screenplayKeys.list(projectId) : ['screenplays'],
        queryFn: async () => {
            if (!projectId) return []
            return screenplayService.getAll(projectId)
        },
        enabled: !!projectId,
    })
}

export const useScreenplayQuery = (projectId?: string, title?: string) => {
    return useQuery({
        queryKey: projectId && title ? screenplayKeys.byTitle(projectId, title) : ['screenplay', 'disabled'],
        queryFn: async () => {
             const data = await screenplayService.getByTitle(projectId!, title!)
             return data ?? null
        },
        enabled: !!projectId && !!title,
    })
}

export const useCreateScreenplayMutation = (options?: HookOptions<void, AppError, ScreenplayEntity>) => {
    return useAppMutation<void, AppError, ScreenplayEntity>({
        mutationFn: (screenplay: ScreenplayEntity) => screenplayService.save(screenplay),
        invalidateKeys: [screenplayKeys.all],
    }, options)
}

export const useUpdateScreenplayMutation = (options?: HookOptions<void, AppError, ScreenplayEntity>) => {
    return useAppMutation<void, AppError, ScreenplayEntity>({
        mutationFn: (screenplay: ScreenplayEntity) => screenplayService.save(screenplay),
        invalidateKeys: [screenplayKeys.all],
    }, options)
}

export const useDeleteScreenplayMutation = (options?: HookOptions<void, AppError, { id: string, projectId: string }>) => {
    return useAppMutation<void, AppError, { id: string, projectId: string }>({
        mutationFn: ({ id, projectId }) => screenplayService.delete(id, projectId),
        invalidateKeys: [screenplayKeys.all],
    }, options)
}
